import { SysCodeDto } from 'src/dtos/code/sysCodeDto';
import {
	defaultKSK2022_R01InspectDto,
	KSK2022_R01InspectDto,
} from '../Dto_KSK2022_R01';

/** 불량사유 공통코드 전체와 기존 저장 내역을 결합해 화면 표시 행을 만든다. */
export const buildKSK2022_R01InspectOptionRows = (
	faultyValueList: SysCodeDto[],
	inspectList: KSK2022_R01InspectDto[],
	getBadCaseName: (faultyValue: SysCodeDto) => string,
): KSK2022_R01InspectDto[] => {
	const existingByBadCase = new Map<string, KSK2022_R01InspectDto>();
	inspectList
		.filter(item => !!item.badCase)
		.forEach(item => {
			const existing = existingByBadCase.get(item.badCase);
			existingByBadCase.set(item.badCase, existing
				? { ...existing, badQty: (Number(existing.badQty) || 0) + (Number(item.badQty) || 0) }
				: item,
			);
		});
	const activeBadCaseSet = new Set(faultyValueList.map(item => item.minorCd));

	const activeRows = faultyValueList.map((faultyValue, index) => {
		const existing = existingByBadCase.get(faultyValue.minorCd);

		return {
			...defaultKSK2022_R01InspectDto,
			...existing,
			valueIndex: index + 1,
			orderIndex: index + 1,
			orderIndexSeq: index + 1,
			badCase: faultyValue.minorCd,
			// FAULTY_VALUE.ref02의 다국어 코드를 조회하고, 번역이 없으면 codeNm을 사용한다.
			badCaseNm: getBadCaseName(faultyValue) || faultyValue.codeNm,
			badQty: Number(existing?.badQty) || 0,
			inserted: false,
			isModified: false,
		};
	});

	// 현재 공통코드에서 제외된 기존 데이터도 유실되지 않도록 목록 하단에 유지한다.
	const inactiveRows = Array.from(existingByBadCase.values())
		.filter(item => !!item.badCase && !activeBadCaseSet.has(item.badCase))
		.map((item, index) => ({
			...defaultKSK2022_R01InspectDto,
			...item,
			valueIndex: activeRows.length + index + 1,
			orderIndex: activeRows.length + index + 1,
			orderIndexSeq: activeRows.length + index + 1,
			badCaseNm: item.badCaseNm || `${item.badCase} (미사용)`,
			badQty: Number(item.badQty) || 0,
			inserted: false,
			isModified: false,
		}));

	return [...activeRows, ...inactiveRows];
};


export interface AggregatedInspectBadQty {
	totalQty: number;
	maxBadCaseQty: number;
	badCaseQtyMap: Map<string, number>;
}

/** badCase 기준으로 badQty를 합산해 집계한다. (동일 badCase는 합산) */
export const aggregateInspectListByBadCase = (
	inspectList: KSK2022_R01InspectDto[],
	deletedValueIndexes: number[] = [],
): AggregatedInspectBadQty => {
	const deletedIndexSet = new Set(deletedValueIndexes.map(value => Number(value)));
	const badCaseQtyMap = new Map<string, number>();

	inspectList
		.filter(inspect =>
			!deletedIndexSet.has(inspect.valueIndex) &&
			!!inspect.badCase
		)
		.forEach(item => {
			const qty = Number(item.badQty) || 0;
			badCaseQtyMap.set(item.badCase, (badCaseQtyMap.get(item.badCase) || 0) + qty);
		});

	const aggregatedQtyList = Array.from(badCaseQtyMap.values());
	const totalQty = aggregatedQtyList.reduce((sum, qty) => sum + qty, 0);
	const maxBadCaseQty = aggregatedQtyList.length > 0
		? Math.max(...aggregatedQtyList)
		: 0;

	return { totalQty, maxBadCaseQty, badCaseQtyMap };
};

export interface ValidateEditBadQtyResult {
	valid: boolean;
	message?: string;
}

/**
 * editKSK2022_R01.badQty가 badCase별 집계 수량 범위 내인지 검증한다.
 * - 최대: badQty는 집계 총수량(totalQty)을 초과할 수 없음
 * - 최소: badQty는 badCase별 집계 수량 중 최대값(maxBadCaseQty)보다 작을 수 없음
 */
export const validateEditBadQtyAgainstInspectList = (
	editBadQty: number,
	inspectList: KSK2022_R01InspectDto[],
	deletedValueIndexes: number[] = [],
): ValidateEditBadQtyResult => {
	const { totalQty, maxBadCaseQty, badCaseQtyMap } = aggregateInspectListByBadCase(
		inspectList,
		deletedValueIndexes,
	);

	if (badCaseQtyMap.size === 0) {
		return { valid: true };
	}

	if (editBadQty > totalQty) {
		return {
			valid: false,
			message: `불량수량은 불량내역 총수량(${totalQty})을 초과할 수 없습니다.`,
		};
	}

	if (editBadQty < maxBadCaseQty) {
		return {
			valid: false,
			message: `불량수량은 불량내역 사유별 최대 수량(${maxBadCaseQty})보다 작을 수 없습니다.`,
		};
	}

	return { valid: true };
};
